// @ts-nocheck
import React from 'react';

interface LibraryItem {
  _id: string;
  title: string;
  description?: string;
  sourceDomain?: string;
  sourceType: string;
  contentType: string;
  originalUrl: string;
  canonicalUrl?: string;
  thumbnailUrl?: string;
  author?: string;
  publisher?: string;
  publishedAt?: string;
  createdAt?: string;
  readState: 'unread' | 'reading' | 'read';
  savedState: 'saved' | 'archived' | 'deleted';
  isFavorite?: boolean;
  tags?: string[];
  note?: string;
}

interface ReaderContentListProps {
  items: LibraryItem[];
  selectedItem: LibraryItem | null;
  onSelectItem: (item: LibraryItem) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedFilter: string; // 'all' | 'article' | 'video' | 'podcast' | 'social'
  onFilterChange: (f: string) => void;
  sortOrder: string; // 'newest' | 'oldest'
  onSortChange: (s: string) => void;
  onMarkAllRead?: () => void;
  isLoading: boolean;
  viewTitle?: string;
}

function formatRelativeDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 2) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function ReaderContentList({
  items = [],
  selectedItem,
  onSelectItem,
  searchQuery,
  onSearchChange,
  selectedFilter,
  onFilterChange,
  sortOrder,
  onSortChange,
  onMarkAllRead,
  isLoading,
  viewTitle = 'Items',
}: ReaderContentListProps) {
  return (
    <section className="reader-content-list-col" aria-label="Reading Content List">
      <div className="reader-content-list-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '0.98rem', fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>
            {viewTitle}
          </h2>
          {onMarkAllRead && items.some(i => i.readState === 'unread') && (
            <button
              className="reader-btn"
              style={{ fontSize: '0.75rem', padding: '2px 8px', minHeight: '26px' }}
              onClick={onMarkAllRead}
              title="Mark visible items as read"
            >
              <i className="fi fi-rr-check" style={{ fontSize: '0.7rem' }} />
              <span>Mark all read</span>
            </button>
          )}
        </div>

        {/* Search Input */}
        <div className="reader-search-input-wrap">
          <i className="fi fi-rr-search" />
          <input
            type="search"
            className="reader-search-input"
            placeholder="Search items, authors, notes... ( / )"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter Pills and Sort */}
        <div className="reader-filter-row">
          <div className="reader-filter-pills" role="tablist" aria-label="Filter content type">
            {['all', 'article', 'video', 'podcast', 'social'].map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={selectedFilter === f}
                className={`reader-filter-pill ${selectedFilter === f ? 'active' : ''}`}
                onClick={() => onFilterChange(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="reader-sort-wrap">
            <select
              className="reader-sort-select"
              value={sortOrder}
              onChange={(e) => onSortChange(e.target.value)}
              aria-label="Sort items by publish date"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
            <i className="fi fi-rr-angle-small-down reader-sort-chevron" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Items Scrollable List */}
      <div className="reader-items-scroll">
        {isLoading ? (
          <div className="reader-empty-state">
            <div style={{ fontSize: '0.9rem', color: 'var(--reader-text-subtle)' }}>
              Loading items…
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="reader-empty-state">
            <i className="fi fi-rr-inbox reader-empty-icon" />
            <div className="reader-empty-title">No items found</div>
            <div className="reader-empty-subtitle">
              {searchQuery
                ? `No results matching "${searchQuery}".`
                : 'Save links or subscribe to RSS feeds to build your digital library.'}
            </div>
          </div>
        ) : (
          items.map((item) => {
            const isSelected = selectedItem && selectedItem._id === item._id;
            const isUnread = item.readState === 'unread';

            return (
              <article
                key={item._id}
                className={`reader-item-card ${isSelected ? 'selected' : ''} ${!isUnread ? 'read' : ''}`}
                onClick={() => onSelectItem(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSelectItem(item);
                  }
                }}
              >
                <div className="reader-item-meta-top">
                  {isUnread && <span className="reader-unread-indicator" title="Unread" />}
                  <span className="reader-item-source">{item.sourceDomain || 'Web'}</span>
                  {item.sourceType && item.sourceType !== 'generic' && (
                    <span className="reader-item-type-badge">{item.sourceType}</span>
                  )}
                  <span style={{ marginLeft: 'auto' }}>
                    {formatRelativeDate(item.publishedAt || item.createdAt)}
                  </span>
                </div>

                <div className="reader-item-main">
                  <div className="reader-item-text-wrap">
                    <h3 className="reader-item-title">{item.title}</h3>
                    {item.description && (
                      <p className="reader-item-excerpt">{item.description}</p>
                    )}

                    {item.tags && item.tags.length > 0 && (
                      <div className="reader-item-tags-row">
                        {item.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="reader-item-tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {item.thumbnailUrl && (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      className="reader-item-thumb"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
